/* eslint-disable */

import { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Error404Layout } from 'dibs-buyer-layout/exports/error404';
import ArrowLeft from 'dibs-icons/exports/legacy/ArrowLeft';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight';
import { CatalogDocument } from './CatalogDocument';
import { CatalogError } from './CatalogError';
import { CatalogHeader } from './CatalogHeader';
import { CatalogMenuBar } from './CatalogMenuBar/CatalogMenuBar';
import { calculatedPageNumberText, getCanvasWidth, isMobilePortrait } from '../catalogHelpers';
import {
    displayRotateBanner,
    hideRotateBanner,
    setCurrentPage,
    setPaperTypeVars,
    setScreenOrientation,
    toggleMenuBar,
    togglePageOverview,
    toggleShareOptions,
    zoomOut,
} from '../actionCreators/uiActionCreators';
import get from 'lodash.get';
import throttle from 'lodash.throttle';
import * as tracking from 'dibs-tracking';
import serverVars from 'server-vars';

import styles from '../styles/catalogLayout.scss';
import { LANDSCAPE, PORTRAIT, MENUBAR_WIDTH } from '../catalogConstants';
import { DESKTOP, TABLET, MOBILE } from '../../utils/deviceType';
import { CatalogSendToFriendModal } from './CatalogSendToFriendModal';

const CDN_HOST = serverVars.get('cdnDomain');

// polyfill so onOutsideClick will work in IE
if (!Element.prototype.matches) {
    Element.prototype.matches =
        Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
}
if (!Element.prototype.closest) {
    Element.prototype.closest = function (s) {
        var el = this;
        if (!document.documentElement.contains(el)) return null;
        do {
            if (el.matches(s)) return el;
            el = el.parentElement || el.parentNode;
        } while (el !== null && el.nodeType === 1);
        return null;
    };
}

export class CatalogLayoutComponent extends Component {
    constructor(props) {
        super(props);

        this.setScreenOrientation = this.setScreenOrientation.bind(this);
        this.setShowMenuBar = this.setShowMenuBar.bind(this);
        this.changePage = this.changePage.bind(this);
        this.onKeyDown = throttle(this.onKeyDown.bind(this), 250);
        this.onOutsideClick = this.onOutsideClick.bind(this);
    }

    componentDidMount() {
        const { setCurrentPage, setPaperTypeVars, viewer } = this.props;
        this.setScreenOrientation();
        setCurrentPage(1);
        setPaperTypeVars(get(viewer, 'catalogPage.type'));

        document.addEventListener('click', this.onOutsideClick);
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('orientationchange', this.setScreenOrientation);
        window.addEventListener('resize', this.setScreenOrientation);
        window.addEventListener('scroll', this.setShowMenuBar);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.onOutsideClick);
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('orientationchange', this.setScreenOrientation);
        window.removeEventListener('resize', this.setScreenOrientation);
        window.removeEventListener('scroll', this.setShowMenuBar);
    }

    setScreenOrientation() {
        const { deviceType, displayRotateBanner, hideRotateBanner, setScreenOrientation } =
            this.props;

        const newScreenOrientation = window.innerHeight > window.innerWidth ? PORTRAIT : LANDSCAPE;
        setScreenOrientation(newScreenOrientation);

        if (deviceType === TABLET) {
            if (newScreenOrientation === PORTRAIT) {
                displayRotateBanner();
            } else if (newScreenOrientation === LANDSCAPE) {
                hideRotateBanner();
            }
        }
    }

    setShowMenuBar(e) {
        const { deviceType, screenOrientation, showMenuBar, toggleMenuBar } = this.props;

        // hide or display menu bar for mobile portrait
        if (isMobilePortrait(deviceType, screenOrientation)) {
            if (
                (e.currentTarget.scrollY < 350 && !showMenuBar) ||
                (e.currentTarget.scrollY >= 350 && showMenuBar)
            ) {
                toggleMenuBar();
            }
        }
    }

    changePage(direction) {
        const {
            currentPage,
            deviceType,
            isPageRight,
            screenOrientation,
            setCurrentPage,
            viewer,
            zoomOut,
        } = this.props;

        const pageCount = get(viewer, 'catalogPage.images.pageCount');
        const isPhonePortrait = isMobilePortrait(deviceType, screenOrientation);

        const pageNumberText = calculatedPageNumberText(
            currentPage,
            pageCount,
            isPhonePortrait,
            isPageRight
        );

        tracking.trackEvent({
            category: 'product catalog',
            action: `pagination - ${direction} button`,
            label: `${pageNumberText}`,
        });

        // this will only get called for desktop, so we do not need to specify isPageLeft or isPageRight
        setCurrentPage(direction === 'previous' ? currentPage - 1 : currentPage + 1);
        zoomOut();
    }

    onKeyDown({ key, code, keyCode }) {
        const { currentPage, deviceType, viewer } = this.props;
        const pageCount = get(viewer, 'catalogPage.images.pageCount');

        // only check for this on desktop
        if (deviceType === DESKTOP) {
            if ([key, code].includes('ArrowRight') || keyCode === 39) {
                // return if trying to go right on last page
                if (currentPage === pageCount) {
                    return null;
                }
                this.changePage('next');
            } else if ([key, code].includes('ArrowLeft') || keyCode === 37) {
                // return if trying to go left on first page
                if (currentPage === 1) {
                    return null;
                }
                this.changePage('previous');
            }
        }
    }

    onOutsideClick(event) {
        const { pageOverviewOpen, shareOptionsOpen, togglePageOverview, toggleShareOptions } =
            this.props;
        // if overview is open and user clicks outside of it, close overview
        if (pageOverviewOpen) {
            if (
                !event.target.closest('.overviewContainer') &&
                !event.target.closest('.gridIconWrapper')
            ) {
                togglePageOverview();
            }
        }

        if (shareOptionsOpen) {
            if (
                !event.target.closest('.shareOptionsPanel') &&
                !event.target.closest('.shareIconWrapper')
            ) {
                toggleShareOptions();
            }
        }
    }

    render() {
        const {
            currentPage,
            deviceType,
            errorOccurred,
            pageTypeId,
            QuickViewImport,
            relayEnvironment,
            rootId,
            sendToFriendModalIsOpen,
            screenOrientation,
            showMenuBar,
            viewer,
        } = this.props;

        const isPhonePortrait = isMobilePortrait(deviceType, screenOrientation);
        const pageCount = get(viewer, 'catalogPage.images.pageCount');

        if (errorOccurred) {
            return <CatalogError />;
        }

        if (!get(viewer, 'catalogPage.serviceId')) {
            return <Error404Layout viewer={viewer} />;
        }

        const catalogTitle = get(viewer, 'catalogPage.title');
        const prefix = get(viewer, 'catalogPage.images.prefix');
        const catalogShareImgURL = get(viewer, 'catalogPage.socialShareURLWithFallback') || '';
        const showArrows = deviceType === DESKTOP;
        const canvasWidth = getCanvasWidth(deviceType, screenOrientation);
        const gridColumns = `${MENUBAR_WIDTH}px ${canvasWidth}px`;

        const canvasContainerStyle = {
            gridTemplateColumns: gridColumns,
            msGridColumns: gridColumns,
            width: '100%',
        };

        return (
            <div>
                <div
                    className={classNames({
                        [styles.canvasContainer]: true,
                        [styles.canvasContainerTablet]: deviceType === TABLET,
                        [styles.canvasNonMobilePortrait]: !isPhonePortrait,
                    })}
                    style={canvasContainerStyle}
                >
                    <CatalogHeader deviceType={deviceType} title={catalogTitle} />
                    {showMenuBar && (
                        <CatalogMenuBar
                            catalogPage={viewer.catalogPage}
                            catalogShareImgURL={catalogShareImgURL}
                            deviceType={deviceType}
                        />
                    )}
                    <div
                        className={classNames({
                            [styles.documentContent]: true,
                            [styles.documentNonMobilePortrait]: !isPhonePortrait,
                        })}
                    >
                        {showArrows && currentPage > 1 && (
                            <div
                                className={classNames({
                                    [styles.arrowContainer]: true,
                                    [styles.arrowLeft]: true,
                                })}
                                onClick={() => this.changePage('previous')}
                            >
                                <ArrowLeft className={styles.pageArrows} />
                            </div>
                        )}
                        <CatalogDocument catalogPage={viewer.catalogPage} deviceType={deviceType} />
                        {showArrows && currentPage < pageCount && (
                            <div
                                className={classNames({
                                    [styles.arrowContainer]: true,
                                    [styles.arrowRight]: true,
                                })}
                                onClick={() => this.changePage('next')}
                            >
                                <ArrowRight className={styles.pageArrows} />
                            </div>
                        )}
                    </div>
                </div>
                {QuickViewImport && (
                    <QuickViewImport
                        deviceType={deviceType}
                        catalogTitle={catalogTitle}
                        relayEnvironment={relayEnvironment}
                    />
                )}
                {sendToFriendModalIsOpen && (
                    <CatalogSendToFriendModal
                        catalogShareImgURL={catalogShareImgURL}
                        catalogTitle={catalogTitle}
                        pageTypeId={pageTypeId}
                        relay={{ environment: relayEnvironment }}
                        rootId={rootId}
                        senderEmail={get(viewer, 'user.profile.email')}
                    />
                )}
            </div>
        );
    }
}

CatalogLayoutComponent.propTypes = {
    currentPage: PropTypes.number,
    deviceType: PropTypes.string,
    displayRotateBanner: PropTypes.func,
    errorOccurred: PropTypes.bool,
    hideRotateBanner: PropTypes.func,
    isPageRight: PropTypes.bool,
    pageOverviewOpen: PropTypes.bool,
    pageTypeId: PropTypes.string,
    relayEnvironment: PropTypes.object,
    rootId: PropTypes.string,
    QuickViewImport: PropTypes.func,
    screenOrientation: PropTypes.string,
    sendToFriendModalIsOpen: PropTypes.bool,
    setCurrentPage: PropTypes.func,
    setPaperTypeVars: PropTypes.func,
    setScreenOrientation: PropTypes.func,
    shareOptionsOpen: PropTypes.bool,
    showMenuBar: PropTypes.bool,
    togglePageOverview: PropTypes.func,
    toggleShareOptions: PropTypes.func,
    viewer: PropTypes.object,
    zoomOut: PropTypes.func,
};

const mapStateToProps = state => {
    return {
        currentPage: state.ui.currentPage,
        errorOccurred: state.ui.errorOccurred,
        isPageRight: state.ui.isPageRight,
        pageOverviewOpen: state.ui.pageOverviewOpen,
        QuickViewImport: state.ui.QuickViewImport,
        sendToFriendModalIsOpen: state.ui.sendToFriendModalIsOpen,
        screenOrientation: state.ui.screenOrientation,
        shareOptionsOpen: state.ui.shareOptionsOpen,
        showMenuBar: state.ui.showMenuBar,
    };
};

export const mapDispatchToProps = dispatch => {
    return {
        displayRotateBanner: () => dispatch(displayRotateBanner()),
        hideRotateBanner: () => dispatch(hideRotateBanner()),
        setCurrentPage: (nextPageNum, pageL, pageR) =>
            dispatch(setCurrentPage(nextPageNum, pageL, pageR)),
        setPaperTypeVars: pageType => dispatch(setPaperTypeVars(pageType)),
        setScreenOrientation: screenOrientation =>
            dispatch(setScreenOrientation(screenOrientation)),
        toggleMenuBar: () => dispatch(toggleMenuBar()),
        togglePageOverview: () => dispatch(togglePageOverview()),
        toggleShareOptions: () => dispatch(toggleShareOptions()),
        zoomOut: () => dispatch(zoomOut()),
    };
};

export const CatalogLayout = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(CatalogLayoutComponent),
    {
        viewer: graphql`
            fragment CatalogLayout_viewer on Viewer {
                ...Error404Layout_viewer
                user(userId: $userId) @include(if: $hasUserId) {
                    profile {
                        email
                    }
                }
                catalogPage(id: $rootId, pageTypeId: $pageTypeId) {
                    serviceId
                    title
                    type
                    socialShareURLWithFallback
                    images {
                        pageCount
                    }
                    ...CatalogDocument_catalogPage
                    ...CatalogMenuBar_catalogPage
                }
            }
        `,
    }
);
